import { useState, useEffect } from "react";
import { Box, Typography, Grid, Button, Alert } from "@mui/material";
import { Event } from "@mui/icons-material";
import { getData } from "../../Services/getData";
import { postData } from "../../Services/postData";
import { endpoints } from "../../Utils/UrlEnum";
import { ICalendarEvent, ICertificateExam } from "../../Utils/Types";
import Calendar from "../Calendar/Calendar";
import styles from "../../Styles/examCalendar.module.css";
import ExamDetails from "./ExamDetails";
import GenericModal from "../GenericModal";
import { Vocabulary } from "../../Utils/Vocabulary";
import Config from "../../Utils/Config";

interface ExamCalendarProps {
  onExamEnroll?: (examId: string) => void;
}

const ExamCalendar = ({ onExamEnroll }: ExamCalendarProps) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [exams, setExams] = useState<ICertificateExam[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedDayExams, setSelectedDayExams] = useState<ICertificateExam[]>(
    []
  );
  const [confirmModal, setConfirmModal] = useState(false);
  const [examToEnroll, setExamToEnroll] = useState<string | null>(null);
  const clientId = localStorage.getItem("clientId");
  const workPointId = localStorage.getItem("userWorkPointId");

  useEffect(() => {
    fetchExams();
    setSelectedDate(null); // Reset selected day when month changes
  }, [currentDate]);

  const fetchExams = async () => {
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const startDate = new Date(year, month, 1).toISOString();
      const endDate = new Date(year, month + 1, 0).toISOString();

      const response = await getData(
        `${endpoints.certificateExams}?clientId=${clientId}&startDate=${startDate}&endDate=${endDate}&workPointId=${workPointId}`
      );
      setExams(response.data || []);
    } catch (error) {
      console.error("Error fetching certificate exams:", error);
      setExams([]);
    }
  };

  const handleEnrollInExam = (examId: string) => {
    setExamToEnroll(examId);
    setConfirmModal(true);
  };

  const confirmEnrollment = async () => {
    if (!examToEnroll) return;

    try {
      await postData(`${endpoints.certificateExams}/${examToEnroll}/enroll`, {
        clientId,
      });
      fetchExams(); // Refresh the data
      onExamEnroll?.(examToEnroll);
      setConfirmModal(false);
      setExamToEnroll(null);
      setSelectedDate(null); // Reset selected date to refresh details
      setCurrentDate(new Date(currentDate)); // Trigger month refresh
    } catch (error) {
      console.error("Error enrolling in exam:", error);
      setConfirmModal(false);
      setExamToEnroll(null);
    }
  };

  const cancelEnrollment = () => {
    setConfirmModal(false);
    setExamToEnroll(null);
  };

  // Check if enrollment is allowed (at least 5 days before exam)
  const isEnrollmentAllowed = (examDate: string) => {
    const exam = new Date(examDate);
    const now = new Date();
    const diffInDays = Math.ceil((exam.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return diffInDays >= Config.examEnrollmentMinDays;
  };

  // Convert exams to calendar events with enrollment status
  const calendarEvents: ICalendarEvent[] = exams.map((exam) => ({
    id: exam._id,
    date: exam.examDate,
    isClientEnrolled: exam.isClientEnrolled,
    canEnroll: isEnrollmentAllowed(exam.examDate),
    ...exam, // Include all exam properties
  }));

  // Handle date selection from calendar
  const handleDateSelect = (date: Date, events: ICalendarEvent[]) => {
    setSelectedDate(date);
    // Convert back to CertificateExam format
    const examEvents = events.map((event) => {
      const { id, date: eventDate, ...examData } = event;
      return {
        _id: id,
        examDate: eventDate,
        ...examData,
      } as ICertificateExam;
    });
    setSelectedDayExams(examEvents);
  };

  // Handle month change from calendar
  const handleMonthChange = (date: Date) => {
    setCurrentDate(date);
  };

  // Custom event indicator renderer with enrolled status
  const renderEventIndicator = (events: ICalendarEvent[]) => {
    const hasEnrolledExam = events.some(event => event.isClientEnrolled);
    const enrolledEvents = events.filter(event => event.isClientEnrolled);
    const availableEvents = events.filter(event => !event.isClientEnrolled);
    
    let tooltipMessage = '';
    if (enrolledEvents.length > 0 && availableEvents.length > 0) {
      tooltipMessage = `${enrolledEvents.length} înscris, ${availableEvents.length} disponibil`;
    } else if (enrolledEvents.length > 0) {
      tooltipMessage = `${enrolledEvents.length} înscris`;
    } else {
      tooltipMessage = `${availableEvents.length} disponibil`;
    }
    
    return (
      <div title={tooltipMessage}>
        <Event 
          fontSize="small" 
          sx={{ 
            color: hasEnrolledExam ? '#4caf50' : 'inherit' // Green for enrolled exams
          }} 
        />
        <Typography 
          variant="caption" 
          sx={{ 
            color: hasEnrolledExam ? '#4caf50' : 'inherit',
            fontWeight: hasEnrolledExam ? 600 : 'normal'
          }}
        >
          {events.length}
        </Typography>
      </div>
    );
  };

  return (
    <Box className={styles.calendarContainer}>
      {/* Enrollment Notice */}
      <Alert 
        severity="info" 
        sx={{ 
          marginBottom: 3,
          borderRadius: 2,
          backgroundColor: 'rgba(46, 58, 89, 0.05)',
          border: '1px solid rgba(46, 58, 89, 0.2)',
          '& .MuiAlert-icon': {
            color: '#2e3a59'
          }
        }}
      >
        <Typography variant="body2" sx={{ color: '#2e3a59', fontWeight: 500 }}>
          {Vocabulary.enrollmentDaysNotice}
        </Typography>
      </Alert>
      
      <Grid container spacing={2} className={styles.calendarGrid}>
        {/* Calendar View */}
        <Grid item xs={12} md={7} className={styles.calendarSection}>
          <Calendar
            events={calendarEvents}
            onDateSelect={handleDateSelect}
            onMonthChange={handleMonthChange}
            initialDate={currentDate}
            renderEventIndicator={renderEventIndicator}
            className={styles.customCalendar}
          />
        </Grid>

        {/* Exam Details */}
        <Grid item xs={12} md={5} className={styles.detailsSection}>
          <ExamDetails
            selectedDate={selectedDate}
            exams={selectedDayExams}
            onEnrollInExam={handleEnrollInExam}
          />
        </Grid>
      </Grid>

      {/* Enrollment Confirmation Modal */}
      <GenericModal
        open={confirmModal}
        onClose={cancelEnrollment}
        title={Vocabulary.examEnrollment}
        actions={
          <>
            <Button variant="outlined" onClick={cancelEnrollment} color="error">
              {Vocabulary.cancel}
            </Button>
            <Button
              variant="contained"
              onClick={confirmEnrollment}
              color="primary"
            >
              {Vocabulary.confirm}
            </Button>
          </>
        }
      >
        <Typography variant="body1" sx={{ mb: 2 }}>
          {Vocabulary.confirmRegistrationMessage}
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {Vocabulary.confirmRegistrationNotice}
        </Typography>
      </GenericModal>
    </Box>
  );
};

export default ExamCalendar;
